import React, { useState, useEffect } from 'react';
import { MapPin, Plus, Edit2, Trash2, X, Search, Phone, Mail, Globe, ExternalLink, Check } from 'lucide-react';
import { supabase } from '../supabase';

const RESOURCE_TYPES = [
  { value: 'parts_supplier', label: 'Parts Supplier' },
  { value: 'equipment_rental', label: 'Equipment Rental' },
  { value: 'subcontractor', label: 'Subcontractor' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'disposal', label: 'Disposal / Recycling' },
  { value: 'other', label: 'Other' }
];

const BUSINESS_TYPES = [
  { value: 'all', label: 'All Business Types' },
  { value: 'garage_door', label: 'Garage Door' },
  { value: 'pest_control', label: 'Pest Control' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'landscaping', label: 'Landscaping' },
  { value: 'roofing', label: 'Roofing' },
  { value: 'painting', label: 'Painting' },
  { value: 'cleaning', label: 'Cleaning' },
  { value: 'general', label: 'General Contractor' }
];

const SUBSCRIPTION_TIERS = [
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'premium', label: 'Premium' },
  { value: 'featured', label: 'Featured' }
];

const SUBSCRIPTION_STATUSES = [
  { value: 'active', label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'expired', label: 'Expired' },
  { value: 'cancelled', label: 'Cancelled' }
];

export default function LocalResourcesAdmin() {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [form, setForm] = useState({
    name: '',
    description: '',
    phone: '',
    email: '',
    website: '',
    address: '',
    latitude: '',
    longitude: '',
    business_types_served: ['all'],
    resource_type: 'parts_supplier',
    subscription_status: 'active',
    subscription_tier: 'free',
    subscription_expires_at: '',
    is_active: true,
    service_radius_miles: 50
  });

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      const { data, error } = await supabase
        .from('local_resources')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResources(data || []);
    } catch (error) {
      console.error('Error loading resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const data = {
        name: form.name,
        description: form.description || null,
        phone: form.phone || null,
        email: form.email || null,
        website: form.website || null,
        address: form.address || null,
        latitude: form.latitude ? parseFloat(form.latitude) : null,
        longitude: form.longitude ? parseFloat(form.longitude) : null,
        business_types_served: form.business_types_served,
        resource_type: form.resource_type,
        subscription_status: form.subscription_status,
        subscription_tier: form.subscription_tier,
        subscription_expires_at: form.subscription_expires_at || null,
        is_active: form.is_active,
        service_radius_miles: parseInt(form.service_radius_miles) || 50
      };

      if (editingId) {
        const { error } = await supabase
          .from('local_resources')
          .update(data)
          .eq('id', editingId);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('local_resources')
          .insert([data]);
        if (error) throw error;
      }

      setShowForm(false);
      setEditingId(null);
      resetForm();
      loadResources();
    } catch (error) {
      alert('Error saving resource: ' + error.message);
    }
  };

  const handleEdit = (resource) => {
    setForm({
      name: resource.name || '',
      description: resource.description || '',
      phone: resource.phone || '',
      email: resource.email || '',
      website: resource.website || '',
      address: resource.address || '',
      latitude: resource.latitude?.toString() || '',
      longitude: resource.longitude?.toString() || '',
      business_types_served: resource.business_types_served || ['all'],
      resource_type: resource.resource_type || 'parts_supplier',
      subscription_status: resource.subscription_status || 'active',
      subscription_tier: resource.subscription_tier || 'free',
      subscription_expires_at: resource.subscription_expires_at?.split('T')[0] || '',
      is_active: resource.is_active !== false,
      service_radius_miles: resource.service_radius_miles || 50
    });
    setEditingId(resource.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this resource? This cannot be undone.')) return;

    try {
      const { error } = await supabase
        .from('local_resources')
        .delete()
        .eq('id', id);
      if (error) throw error;
      loadResources();
    } catch (error) {
      alert('Error deleting resource: ' + error.message);
    }
  };

  const handleToggleActive = async (resource) => {
    try {
      const { error } = await supabase
        .from('local_resources')
        .update({ is_active: !resource.is_active })
        .eq('id', resource.id);
      if (error) throw error;
      loadResources();
    } catch (error) {
      alert('Error updating resource: ' + error.message);
    }
  };

  const handleBusinessTypeToggle = (value) => {
    setForm(prev => {
      let newTypes = [...prev.business_types_served];

      if (value === 'all') {
        // If selecting 'all', clear others and just use 'all'
        return { ...prev, business_types_served: ['all'] };
      }

      // Remove 'all' if selecting a specific type
      newTypes = newTypes.filter(t => t !== 'all');

      if (newTypes.includes(value)) {
        newTypes = newTypes.filter(t => t !== value);
      } else {
        newTypes.push(value);
      }

      // If no types selected, default to 'all'
      if (newTypes.length === 0) {
        newTypes = ['all'];
      }

      return { ...prev, business_types_served: newTypes };
    });
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      phone: '',
      email: '',
      website: '',
      address: '',
      latitude: '',
      longitude: '',
      business_types_served: ['all'],
      resource_type: 'parts_supplier',
      subscription_status: 'active',
      subscription_tier: 'free',
      subscription_expires_at: '',
      is_active: true,
      service_radius_miles: 50
    });
  };

  // Filter resources
  const filteredResources = resources.filter(resource => {
    const matchesSearch = !searchQuery ||
      resource.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.address?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      resource.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = !filterType || resource.resource_type === filterType;
    const matchesStatus = !filterStatus || resource.subscription_status === filterStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const getResourceTypeLabel = (value) => {
    return RESOURCE_TYPES.find(t => t.value === value)?.label || value;
  };

  const getBusinessTypesLabel = (types) => {
    if (!types || types.length === 0 || types.includes('all')) return 'All';
    return types.map(t => BUSINESS_TYPES.find(b => b.value === t)?.label || t).join(', ');
  };

  if (loading) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#888' }}>
        Loading resources...
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div>
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: '#e0e0e0',
            marginBottom: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem'
          }}>
            <MapPin size={28} />
            Local Resources
          </h1>
          <p style={{ color: '#888', fontSize: '0.9rem' }}>
            Manage vendor directory - parts suppliers, equipment rental, subcontractors, and more
          </p>
        </div>

        <button
          onClick={() => {
            resetForm();
            setEditingId(null);
            setShowForm(true);
          }}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.75rem 1.25rem',
            background: '#ff6b35',
            border: 'none',
            borderRadius: '8px',
            color: '#fff',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          <Plus size={18} />
          Add Resource
        </button>
      </div>

      {/* Summary Stats */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '1rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #ff6b35'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Total Resources
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#ff6b35' }}>
            {resources.length}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #4ecca3'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Active
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#4ecca3' }}>
            {resources.filter(r => r.is_active).length}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #9b59b6'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            Premium
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#9b59b6' }}>
            {resources.filter(r => r.subscription_tier === 'premium' || r.subscription_tier === 'featured').length}
          </div>
        </div>
        <div style={{
          background: '#1a1a1a',
          borderRadius: '10px',
          padding: '1rem',
          border: '1px solid #2a2a2a',
          borderLeft: '4px solid #3498db'
        }}>
          <div style={{ color: '#888', fontSize: '0.75rem', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
            With Location
          </div>
          <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#3498db' }}>
            {resources.filter(r => r.latitude && r.longitude).length}
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap'
      }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <Search size={18} style={{
            position: 'absolute',
            left: '0.75rem',
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#666'
          }} />
          <input
            type="text"
            placeholder="Search by name, address, or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem 0.75rem 0.75rem 2.5rem',
              background: '#1a1a1a',
              border: '1px solid #2a2a2a',
              borderRadius: '8px',
              color: '#e0e0e0',
              fontSize: '0.9rem'
            }}
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          style={{
            padding: '0.75rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            fontSize: '0.9rem',
            minWidth: '160px'
          }}
        >
          <option value="">All Types</option>
          {RESOURCE_TYPES.map(type => (
            <option key={type.value} value={type.value}>{type.label}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{
            padding: '0.75rem',
            background: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            color: '#e0e0e0',
            fontSize: '0.9rem',
            minWidth: '140px'
          }}
        >
          <option value="">All Statuses</option>
          {SUBSCRIPTION_STATUSES.map(status => (
            <option key={status.value} value={status.value}>{status.label}</option>
          ))}
        </select>
      </div>

      {/* Resources List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {filteredResources.length === 0 ? (
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #2a2a2a',
            padding: '3rem',
            textAlign: 'center',
            color: '#888'
          }}>
            <MapPin size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
            <p>{resources.length === 0 ? 'No resources yet. Add your first resource to get started.' : 'No resources match your filters.'}</p>
          </div>
        ) : (
          filteredResources.map(resource => (
            <div
              key={resource.id}
              style={{
                background: '#1a1a1a',
                borderRadius: '12px',
                border: '1px solid #2a2a2a',
                padding: '1.25rem',
                opacity: resource.is_active ? 1 : 0.6
              }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '1rem',
                flexWrap: 'wrap',
                gap: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    background: 'linear-gradient(135deg, #ff6b35, #f7931e)',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <MapPin size={24} color="#fff" />
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <h3 style={{
                        fontSize: '1.15rem',
                        fontWeight: '700',
                        color: '#e0e0e0',
                        margin: 0
                      }}>
                        {resource.name}
                      </h3>
                      <span style={{
                        padding: '0.2rem 0.5rem',
                        background: '#3498db20',
                        color: '#3498db',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '600'
                      }}>
                        {getResourceTypeLabel(resource.resource_type)}
                      </span>
                      <span style={{
                        padding: '0.2rem 0.5rem',
                        background: resource.subscription_tier === 'featured' ? '#9b59b620' :
                                   resource.subscription_tier === 'premium' ? '#f39c1220' :
                                   '#2a2a2a',
                        color: resource.subscription_tier === 'featured' ? '#9b59b6' :
                               resource.subscription_tier === 'premium' ? '#f39c12' :
                               '#888',
                        borderRadius: '4px',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        textTransform: 'uppercase'
                      }}>
                        {resource.subscription_tier}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '1rem',
                      fontSize: '0.8rem',
                      color: '#888',
                      marginTop: '0.35rem',
                      flexWrap: 'wrap'
                    }}>
                      {resource.address && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <MapPin size={12} />
                          {resource.address}
                        </span>
                      )}
                      {resource.service_radius_miles && (
                        <span>{resource.service_radius_miles} mile radius</span>
                      )}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {resource.website && (
                    <a
                      href={resource.website.startsWith('http') ? resource.website : `https://${resource.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '0.5rem',
                        background: '#2a2a2a',
                        border: 'none',
                        borderRadius: '6px',
                        color: '#3498db',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <ExternalLink size={16} />
                    </a>
                  )}
                  <button
                    onClick={() => handleEdit(resource)}
                    style={{
                      padding: '0.5rem',
                      background: '#2a2a2a',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#888',
                      cursor: 'pointer'
                    }}
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => handleToggleActive(resource)}
                    style={{
                      padding: '0.5rem 0.75rem',
                      background: resource.is_active ? '#4ecca320' : '#3a3a3a',
                      border: 'none',
                      borderRadius: '6px',
                      color: resource.is_active ? '#4ecca3' : '#888',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}
                  >
                    {resource.is_active ? 'Active' : 'Inactive'}
                  </button>
                  <button
                    onClick={() => handleDelete(resource.id)}
                    style={{
                      padding: '0.5rem',
                      background: '#2a1a1a',
                      border: 'none',
                      borderRadius: '6px',
                      color: '#e74c3c',
                      cursor: 'pointer'
                    }}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                gap: '0.75rem'
              }}>
                {/* Contact Info */}
                <div style={{
                  background: '#242424',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  gridColumn: 'span 2'
                }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#666',
                    marginBottom: '0.35rem',
                    textTransform: 'uppercase'
                  }}>
                    Contact
                  </div>
                  <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                    {resource.phone && (
                      <a href={`tel:${resource.phone}`} style={{ color: '#4ecca3', display: 'flex', alignItems: 'center', gap: '0.25rem', textDecoration: 'none' }}>
                        <Phone size={14} />
                        {resource.phone}
                      </a>
                    )}
                    {resource.email && (
                      <a href={`mailto:${resource.email}`} style={{ color: '#3498db', display: 'flex', alignItems: 'center', gap: '0.25rem', textDecoration: 'none' }}>
                        <Mail size={14} />
                        {resource.email}
                      </a>
                    )}
                    {resource.website && (
                      <a href={resource.website.startsWith('http') ? resource.website : `https://${resource.website}`} target="_blank" rel="noopener noreferrer" style={{ color: '#9b59b6', display: 'flex', alignItems: 'center', gap: '0.25rem', textDecoration: 'none' }}>
                        <Globe size={14} />
                        Website
                      </a>
                    )}
                    {!resource.phone && !resource.email && !resource.website && (
                      <span style={{ color: '#666' }}>No contact info</span>
                    )}
                  </div>
                </div>

                {/* Business Types */}
                <div style={{
                  background: '#242424',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#666',
                    marginBottom: '0.35rem',
                    textTransform: 'uppercase'
                  }}>
                    Serves
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#e0e0e0' }}>
                    {getBusinessTypesLabel(resource.business_types_served)}
                  </div>
                </div>

                {/* Location */}
                <div style={{
                  background: '#242424',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#666',
                    marginBottom: '0.35rem',
                    textTransform: 'uppercase'
                  }}>
                    Coordinates
                  </div>
                  <div style={{ fontSize: '0.85rem', color: resource.latitude ? '#4ecca3' : '#666' }}>
                    {resource.latitude && resource.longitude
                      ? `${resource.latitude.toFixed(4)}, ${resource.longitude.toFixed(4)}`
                      : 'Not set'}
                  </div>
                </div>

                {/* Subscription */}
                <div style={{
                  background: '#242424',
                  borderRadius: '8px',
                  padding: '0.75rem'
                }}>
                  <div style={{
                    fontSize: '0.7rem',
                    color: '#666',
                    marginBottom: '0.35rem',
                    textTransform: 'uppercase'
                  }}>
                    Status
                  </div>
                  <div style={{
                    fontSize: '0.85rem',
                    color: resource.subscription_status === 'active' ? '#4ecca3' :
                           resource.subscription_status === 'expired' ? '#e74c3c' : '#f39c12'
                  }}>
                    {resource.subscription_status}
                    {resource.subscription_expires_at && (
                      <span style={{ color: '#888', fontSize: '0.75rem', marginLeft: '0.5rem' }}>
                        (exp: {new Date(resource.subscription_expires_at).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {resource.description && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  background: '#242424',
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  color: '#888'
                }}>
                  {resource.description}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            background: '#1a1a1a',
            borderRadius: '12px',
            border: '1px solid #3a3a3a',
            padding: '1.5rem',
            maxWidth: '700px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.25rem'
            }}>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#e0e0e0' }}>
                {editingId ? 'Edit Resource' : 'Add Resource'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer'
                }}
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Basic Info */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Basic Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div style={{ gridColumn: 'span 2' }}>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Business Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Resource Type *
                    </label>
                    <select
                      value={form.resource_type}
                      onChange={(e) => setForm({ ...form, resource_type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      {RESOURCE_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Service Radius (miles)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="500"
                      value={form.service_radius_miles}
                      onChange={(e) => setForm({ ...form, service_radius_miles: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                    Description
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    style={{
                      width: '100%',
                      padding: '0.6rem',
                      background: '#1a1a1a',
                      border: '1px solid #3a3a3a',
                      borderRadius: '6px',
                      color: '#e0e0e0',
                      fontSize: '0.9rem',
                      resize: 'vertical'
                    }}
                  />
                </div>
              </div>

              {/* Contact Info */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Contact Information
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Phone
                    </label>
                    <input
                      type="text"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="(555) 123-4567"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Email
                    </label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Website
                    </label>
                    <input
                      type="text"
                      value={form.website}
                      onChange={(e) => setForm({ ...form, website: e.target.value })}
                      placeholder="www.example.com"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Address
                    </label>
                    <input
                      type="text"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      placeholder="123 Main St, City, ST 12345"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Location */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                  Location Coordinates
                </h3>
                <p style={{ color: '#888', fontSize: '0.8rem', marginBottom: '1rem' }}>
                  Used for proximity filtering. You can get coordinates from Google Maps.
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Latitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={form.latitude}
                      onChange={(e) => setForm({ ...form, latitude: e.target.value })}
                      placeholder="e.g., 33.7490"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Longitude
                    </label>
                    <input
                      type="number"
                      step="any"
                      value={form.longitude}
                      onChange={(e) => setForm({ ...form, longitude: e.target.value })}
                      placeholder="e.g., -84.3880"
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Business Types Served */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Business Types Served
                </h3>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {BUSINESS_TYPES.map(type => (
                    <button
                      key={type.value}
                      type="button"
                      onClick={() => handleBusinessTypeToggle(type.value)}
                      style={{
                        padding: '0.5rem 0.75rem',
                        background: form.business_types_served.includes(type.value) ? '#ff6b3530' : '#1a1a1a',
                        border: `1px solid ${form.business_types_served.includes(type.value) ? '#ff6b35' : '#3a3a3a'}`,
                        borderRadius: '6px',
                        color: form.business_types_served.includes(type.value) ? '#ff6b35' : '#888',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.35rem'
                      }}
                    >
                      {form.business_types_served.includes(type.value) && <Check size={14} />}
                      {type.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Subscription */}
              <div style={{
                background: '#242424',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <h3 style={{ color: '#e0e0e0', fontSize: '0.9rem', marginBottom: '1rem' }}>
                  Subscription
                </h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Tier
                    </label>
                    <select
                      value={form.subscription_tier}
                      onChange={(e) => setForm({ ...form, subscription_tier: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      {SUBSCRIPTION_TIERS.map(tier => (
                        <option key={tier.value} value={tier.value}>{tier.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Status
                    </label>
                    <select
                      value={form.subscription_status}
                      onChange={(e) => setForm({ ...form, subscription_status: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    >
                      {SUBSCRIPTION_STATUSES.map(status => (
                        <option key={status.value} value={status.value}>{status.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '0.35rem', color: '#aaa', fontSize: '0.85rem' }}>
                      Expires At
                    </label>
                    <input
                      type="date"
                      value={form.subscription_expires_at}
                      onChange={(e) => setForm({ ...form, subscription_expires_at: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '0.6rem',
                        background: '#1a1a1a',
                        border: '1px solid #3a3a3a',
                        borderRadius: '6px',
                        color: '#e0e0e0',
                        fontSize: '0.9rem'
                      }}
                    />
                  </div>
                </div>

                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  color: '#e0e0e0',
                  cursor: 'pointer',
                  fontSize: '0.9rem'
                }}>
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    style={{ width: '18px', height: '18px', accentColor: '#4ecca3' }}
                  />
                  Active (visible to users)
                </label>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                  }}
                  style={{
                    flex: 1,
                    padding: '0.75rem',
                    background: '#2a2a2a',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#e0e0e0',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    flex: 2,
                    padding: '0.75rem',
                    background: '#ff6b35',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#fff',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {editingId ? 'Save Changes' : 'Add Resource'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
